import WebsiteMigrationsRepositoryInterface, {
  OrchestratorActionStatus,
  OrchestratorActionType,
  WebsiteMigration,
  WebsiteMigrationsResponse,
} from '@/services/WebsiteMigrations/interfaces';
import { AxiosInstance } from 'axios';

export default class WebsiteMigrationsRepository implements WebsiteMigrationsRepositoryInterface {
  constructor(
    private readonly axios: AxiosInstance,
  ) {
  }

  public async getAllMigrations(
    page: number,
    itemsPerPage: number,
    sourceWebsiteId?: number,
    targetWebsiteId?: number,
    type?: string,
    status?: string,
  ): Promise<WebsiteMigrationsResponse> {
    const query = new URLSearchParams();
    query.set('page', String(page));
    query.set('itemsPerPage', String(itemsPerPage));

    if (sourceWebsiteId) {
      query.set('sourceWebsiteId', String(sourceWebsiteId));
    }

    if (targetWebsiteId) {
      query.set('targetWebsiteId', String(targetWebsiteId));
    }

    if (type) {
      query.set('type', type);
    }

    if (status) {
      query.set('status', status);
    }

    const response = await this.axios.get(`/websites/migrations?${query.toString()}`);

    return response.data;
  }

  public async getMigration(id: number): Promise<WebsiteMigration> {
    const response = await this.axios.get(`/websites/migrations/${id}`);

    return response.data;
  }

  public async renameWebsite(id: number, newDomain: string, excludeActions: string[]): Promise<void> {
    await this.axios.patch(`/websites/${id}/rename`, { newDomain, excludeActions });
  }

  public async getMigrationsBySourceAndTargetGroups(
    sourceGroupId?: number,
    targetGroupId?: number,
  ): Promise<WebsiteMigration[]> {
    if ((!sourceGroupId || sourceGroupId === -1) && (!targetGroupId || targetGroupId === -1)) {
      throw new Error('No source and target groups found');
    }

    const query = new URLSearchParams();
    if (sourceGroupId && sourceGroupId !== -1) {
      query.set('sourceGroupId', String(sourceGroupId));
    }

    if (targetGroupId && targetGroupId !== -1) {
      query.set('targetGroupId', String(targetGroupId));
    }

    const id = (!!targetGroupId && targetGroupId !== -1) ? targetGroupId : sourceGroupId;

    const response = await this.axios.get(`/websites/groups/${id}/migration?${query.toString()}`);

    return response.data.migrations;
  }

  public async updateMigrationActionStatus(
    migrationId: number,
    actionId: number,
    actionType: OrchestratorActionType,
    status: OrchestratorActionStatus,
  ): Promise<void> {
    await this.axios.patch(
      `/websites/migrations/${migrationId}/actions/${actionId}`,
      {
        actionType,
        status,
      },
    );
  }

  public async createMigration(
    sourceWebsiteId: number,
    sourceGroupId: number,
    sourceServerId: string,
    targetServerId: string,
    targetGroupId: number,
  ): Promise<void> {
    await this.axios.post(`/websites/${sourceWebsiteId}/migrate`, {
      sourceWebsiteId,
      sourceGroupId,
      sourceServerId,
      targetServerId,
      targetGroupId,
    });
  }
}
